import React, { useEffect, useMemo } from 'react';
import {
  Alert, Col, Form, Row,
} from 'react-bootstrap';
import {
  FieldPath, FieldValues, UseFormReturn, useWatch,
} from 'react-hook-form';

import {
  BurnBedsFormFields,
  computeBurnBedTotals,
  formatBedDisplay,
  getBurnBedsStaffingValidation,
  toNumberOrNull,
} from './burnBedsUtils';

const EDITABLE_BURN_BED_KEYS: (keyof BurnBedsFormFields)[] = [
  'burnLicensedAdultBeds',
  'burnLicensedPediatricBeds',
  'burnStaffedIcuAdultBeds',
  'burnStaffedIcuPediatricBeds',
  'burnStaffedNonIcuAdultBeds',
  'burnStaffedNonIcuPediatricBeds',
];

const pickBurnBedFields = (
  source: Record<string, unknown> | undefined,
): Partial<BurnBedsFormFields> => {
  if (!source) return {};
  return {
    burnLicensedAdultBeds: toNumberOrNull(source.burnLicensedAdultBeds),
    burnLicensedPediatricBeds: toNumberOrNull(source.burnLicensedPediatricBeds),
    burnLicensedTotalBeds: toNumberOrNull(source.burnLicensedTotalBeds),
    burnStaffedIcuAdultBeds: toNumberOrNull(source.burnStaffedIcuAdultBeds),
    burnStaffedIcuPediatricBeds: toNumberOrNull(source.burnStaffedIcuPediatricBeds),
    burnStaffedIcuTotalBeds: toNumberOrNull(source.burnStaffedIcuTotalBeds),
    burnStaffedNonIcuAdultBeds: toNumberOrNull(source.burnStaffedNonIcuAdultBeds),
    burnStaffedNonIcuPediatricBeds: toNumberOrNull(source.burnStaffedNonIcuPediatricBeds),
    burnStaffedNonIcuTotalBeds: toNumberOrNull(source.burnStaffedNonIcuTotalBeds),
    burnStaffedTotalAdultBeds: toNumberOrNull(source.burnStaffedTotalAdultBeds),
    burnStaffedTotalPediatricBeds: toNumberOrNull(source.burnStaffedTotalPediatricBeds),
    burnStaffedTotalBeds: toNumberOrNull(source.burnStaffedTotalBeds),
    pediatricPatientDefinition: source.pediatricPatientDefinition as string | null,
  };
};

type BurnBedsRowConfig = {
  label: string;
  adult: keyof BurnBedsFormFields;
  pediatric: keyof BurnBedsFormFields;
  total: keyof BurnBedsFormFields;
  editable: boolean;
};

const BURN_BEDS_ROWS: BurnBedsRowConfig[] = [
  {
    label: 'Licensed',
    adult: 'burnLicensedAdultBeds',
    pediatric: 'burnLicensedPediatricBeds',
    total: 'burnLicensedTotalBeds',
    editable: true,
  },
  {
    label: 'Staffed ICU',
    adult: 'burnStaffedIcuAdultBeds',
    pediatric: 'burnStaffedIcuPediatricBeds',
    total: 'burnStaffedIcuTotalBeds',
    editable: true,
  },
  {
    label: 'Staffed Non ICU',
    adult: 'burnStaffedNonIcuAdultBeds',
    pediatric: 'burnStaffedNonIcuPediatricBeds',
    total: 'burnStaffedNonIcuTotalBeds',
    editable: true,
  },
  {
    label: 'Staffed Total',
    adult: 'burnStaffedTotalAdultBeds',
    pediatric: 'burnStaffedTotalPediatricBeds',
    total: 'burnStaffedTotalBeds',
    editable: false,
  },
];

const COLUMN_HEADERS = ['Adult', 'Pediatric', 'Total'] as const;

const ColumnHeaders: React.FC = () => (
  <Row className="g-2 mb-2 align-items-end">
    <Col xs={12} sm={3} />
    {COLUMN_HEADERS.map((header) => (
      <Col key={header} xs={4} sm={3} className="text-muted small fw-semibold">
        {header}
      </Col>
    ))}
  </Row>
);

type Props<T extends FieldValues = FieldValues> = {
  form: UseFormReturn<T>;
  namePrefix?: string;
  editing?: boolean;
  disabled?: boolean;
  readOnlyValues?: Partial<BurnBedsFormFields>;
  onStaffingValidationChange?: (isValid: boolean) => void;
};

const BurnBedsGrid = <T extends FieldValues>({
  form,
  namePrefix = 'benchmark.',
  editing = true,
  disabled = false,
  readOnlyValues,
  onStaffingValidationChange,
}: Props<T>) => {
  const prefix = namePrefix;
  const fieldName = (key: keyof BurnBedsFormFields) => (
    prefix ? `${prefix}${key}` : key
  ) as FieldPath<T>;
  const watchRoot = prefix.endsWith('.') ? prefix.slice(0, -1) : prefix;

  const nestedWatch = useWatch({
    control: form.control,
    name: (watchRoot || undefined) as FieldPath<T>,
  });
  const flatWatch = useWatch({
    control: form.control,
    name: EDITABLE_BURN_BED_KEYS.map((key) => fieldName(key)) as FieldPath<T>[],
  });
  const watched = pickBurnBedFields(
    watchRoot
      ? (nestedWatch as Record<string, unknown>)
      : Object.fromEntries(
        EDITABLE_BURN_BED_KEYS.map((key, index) => [key, flatWatch[index]]),
      ),
  );

  const displayValues = editing ? watched : readOnlyValues;
  const totals = computeBurnBedTotals(displayValues ?? {});

  const staffingValidation = useMemo(
    () => getBurnBedsStaffingValidation(displayValues ?? {}),
    [displayValues],
  );

  useEffect(() => {
    onStaffingValidationChange?.(editing ? staffingValidation.isValid : true);
  }, [editing, onStaffingValidationChange, staffingValidation.isValid]);

  useEffect(() => {
    if (!editing) return;

    const source = watchRoot
      ? pickBurnBedFields(
        form.getValues(watchRoot as FieldPath<T>) as Record<string, unknown>,
      )
      : pickBurnBedFields(
        Object.fromEntries(
          EDITABLE_BURN_BED_KEYS.map((key) => [
            key,
            form.getValues((prefix ? `${prefix}${key}` : key) as FieldPath<T>),
          ]),
        ),
      );

    const nextTotals = computeBurnBedTotals(source);
    (Object.entries(nextTotals) as [keyof BurnBedsFormFields, number | null][]).forEach(
      ([key, value]) => {
        form.setValue(
          (prefix ? `${prefix}${key}` : key) as FieldPath<T>,
          value as never,
          {
            shouldDirty: false,
            shouldValidate: false,
          },
        );
      },
    );
  }, [
    editing,
    form,
    prefix,
    watchRoot,
    watched?.burnLicensedAdultBeds,
    watched?.burnLicensedPediatricBeds,
    watched?.burnStaffedIcuAdultBeds,
    watched?.burnStaffedIcuPediatricBeds,
    watched?.burnStaffedNonIcuAdultBeds,
    watched?.burnStaffedNonIcuPediatricBeds,
  ]);

  const getDisplayValue = (
    key: keyof BurnBedsFormFields,
    row: BurnBedsRowConfig,
    column: 'adult' | 'pediatric' | 'total',
  ) => {
    const isCalculated = column === 'total' || !row.editable;
    if (isCalculated) {
      const fromTotals = (totals as Partial<Record<keyof BurnBedsFormFields, number | null>>)[key];
      return formatBedDisplay(fromTotals ?? toNumberOrNull(displayValues?.[key]));
    }
    return formatBedDisplay(toNumberOrNull(displayValues?.[key]));
  };

  const renderCell = (
    row: BurnBedsRowConfig,
    column: 'adult' | 'pediatric' | 'total',
  ) => {
    const key = row[column];
    const isCalculated = column === 'total' || !row.editable;
    const name = fieldName(key);
    const display = getDisplayValue(key, row, column);
    const hasStaffingError = !!staffingValidation.fieldErrors[key];

    if (!editing) {
      return (
        <Form.Control
          type="text"
          readOnly
          disabled
          style={isCalculated ? { backgroundColor: '#e2e6ea' } : undefined}
          className={isCalculated ? undefined : 'bg-light'}
          value={display}
          tabIndex={-1}
        />
      );
    }

    if (isCalculated) {
      return (
        <Form.Control
          type="number"
          readOnly
          style={{ backgroundColor: '#e2e6ea' }}
          value={display}
          tabIndex={-1}
        />
      );
    }

    return (
      <Form.Control
        type="number"
        inputMode="numeric"
        step={1}
        min={0}
        disabled={disabled}
        isInvalid={hasStaffingError}
        {...form.register(name, { setValueAs: toNumberOrNull })}
      />
    );
  };

  return (
    <div className="burn-beds-grid">
      <h5 className="fw-bold mb-3">Burn Beds</h5>

      {editing && !staffingValidation.isValid && (
        <Alert variant="danger" className="mb-3 py-2">
          <div className="fw-semibold mb-1">Burn beds staffing limits</div>
          <ul className="mb-0 ps-3 small">
            {staffingValidation.messages.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </Alert>
      )}

      {BURN_BEDS_ROWS.map((row, index) => (
        <React.Fragment key={row.label}>
          {(index === 0 || index === 1) && <ColumnHeaders />}
          <Row className="g-2 mb-3 align-items-center">
            <Col xs={12} sm={3} className="fw-semibold">
              {row.label}
            </Col>
            <Col xs={4} sm={3}>{renderCell(row, 'adult')}</Col>
            <Col xs={4} sm={3}>{renderCell(row, 'pediatric')}</Col>
            <Col xs={4} sm={3}>{renderCell(row, 'total')}</Col>
          </Row>
        </React.Fragment>
      ))}

      <Form.Group className="mt-4">
        <Form.Label className="fw-bold">
          How do you define a pediatric patient?
        </Form.Label>
        {!editing ? (
          <Form.Control
            as="textarea"
            rows={2}
            readOnly
            disabled
            className="bg-light"
            value={readOnlyValues?.pediatricPatientDefinition ?? ''}
            tabIndex={-1}
          />
        ) : (
          <Form.Control
            as="textarea"
            rows={2}
            disabled={disabled}
            {...form.register(fieldName('pediatricPatientDefinition'))}
          />
        )}
      </Form.Group>
    </div>
  );
};

export default BurnBedsGrid;
